import React, {
  Children,
  isValidElement,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clsx from 'clsx';
import { parse } from 'querystring';

import { config } from '@utils/config';
import {
  experimentsToTrigger,
  isOptimizeExperimentName,
  optimizeEvents,
  optimizeExperiments,
  refreshOptimizeVariants,
} from '@utils/optimize';
import { getFixedQueryString } from '@utils/query';

import { saveOptimizeVariants as saveVariantsToSurvey } from '@survey/core/utils/storage';

import { gtag } from '../gtm';

import classes from './google-optimize.module.scss';

/**
 * --- PROVIDER ---
 */
type GoogleOptimizeContextValue = {
  optimizeMounted: boolean;
  optimizeVariants: { [key: string]: string };
};

const optimizeMountedDefault = !config.GTM_ENABLED;

export const GoogleOptimizeContext =
  React.createContext<GoogleOptimizeContextValue>({
    optimizeMounted: optimizeMountedDefault,
    optimizeVariants: {},
  });

type Props = PropsWithChildren<{}>;

const defaultState: GoogleOptimizeContextValue = {
  optimizeMounted: optimizeMountedDefault,
  optimizeVariants: {},
};

const validateVariants = (input: any): input is Record<string, string> => {
  if (typeof input !== 'object') return false;

  return !Object.entries(input).some(
    ([key, value]) => typeof key !== 'string' || typeof value !== 'string',
  );
};

const parseVariantsFromQuery = () => {
  // NextJS is stupid and first render on client is done without Query
  // There are hacks to make query available, but they require changes made to all pages
  // This way we just parse the query using the same package, getting the same result
  if (typeof location !== 'undefined') {
    const query = parse(
      getFixedQueryString(location.search.replace(/^\?/, '')),
    );

    if (query.optimizeVariants && !Array.isArray(query.optimizeVariants)) {
      try {
        const variants = JSON.parse(query.optimizeVariants);

        if (!validateVariants(variants)) return;

        // Translate named variants into IDs
        // this allows us to use something like this:
        // { "estimateRequalification": "1" }
        // in URL, so we don't need to search for the experiment ID
        const mappedVariants = Object.entries(variants).reduce<
          Record<string, string>
        >((acc, [key, value]) => {
          if (isOptimizeExperimentName(key))
            return {
              ...acc,
              [optimizeExperiments[key]]: value,
            };

          return {
            ...acc,
            [key]: value,
          };
        }, {});

        return mappedVariants;
      } catch (e) {
        // Pass
      }
    }
  }
};

export const GoogleOptimizeProvider = ({ children }: Props) => {
  const [optimizeState, setOptimizeState] = useState(defaultState);
  const checkTimerRef = useRef<number | undefined>();

  const queryVariants = useRef(parseVariantsFromQuery());

  const checkOptimize = useCallback(() => {
    if (queryVariants.current) {
      const newVariants = queryVariants.current;

      setOptimizeState({
        optimizeMounted: true,
        optimizeVariants: newVariants,
      });

      saveVariantsToSurvey(newVariants);

      optimizeEvents.emit('variantsChanged', newVariants);

      // No need to do anything, we already resolved variants
      return;
    }
    // Already mounted
    if (optimizeState.optimizeMounted) return;

    // Check if GTM & Optimize are initialized
    if (window.google_optimize && window.dataLayer) {
      gtag('event', 'optimize.callback', {
        callback: (value: string, name: string) => {
          setOptimizeState((state) => {
            const newVariants = {
              ...state.optimizeVariants,
              [name]: value,
            };

            saveVariantsToSurvey(newVariants);

            optimizeEvents.emit('variantsChanged', newVariants);

            return {
              optimizeMounted: true,
              optimizeVariants: newVariants,
            };
          });
        },
      });

      setOptimizeState((state) => ({ ...state, optimizeMounted: true }));

      // Some experiments are to be triggered because optimize wasn't loaded yet
      if (experimentsToTrigger.length) {
        let experimentTrigger = experimentsToTrigger.shift();
        while (experimentTrigger) {
          refreshOptimizeVariants(experimentTrigger);

          experimentTrigger = experimentsToTrigger.shift();
        }
      }

      return;
    }

    // Using `window` to make sure Typescript knows we are using browser `setTimeout`
    // not node js `setTimeout` (they have different return types)
    checkTimerRef.current = window.setTimeout(() => checkOptimize(), 50);
  }, [optimizeState.optimizeMounted]);

  // Check optimize on mount and make sure to repopulate `queryVariants`
  useEffect(() => {
    checkOptimize();

    return () => {
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    };
  }, []);

  // Check optimize on `optimizeTriggered` event
  // needs to be in separate `useEffect` because of `checkOptimize` dependency
  useEffect(() => {
    optimizeEvents.addListener('optimizeTriggered', checkOptimize);

    return () => {
      optimizeEvents.removeListener('optimizeTriggered', checkOptimize);
    };
  }, [checkOptimize]);

  return (
    <GoogleOptimizeContext.Provider value={optimizeState}>
      {children}
    </GoogleOptimizeContext.Provider>
  );
};

/**
 * --- WRAPPER ---
 */
type GoogleOptimizeWrapperProps = PropsWithChildren<{
  preventText?: boolean;
  preventDisplay?: boolean;
  triggerEvent?: string;
}>;

export const GoogleOptimizeWrapper = ({
  children,
  preventText,
  preventDisplay,
  triggerEvent,
}: GoogleOptimizeWrapperProps) => {
  const { optimizeMounted } = useContext(GoogleOptimizeContext);

  const [gaMountedPreventDisplay, setGaMountedPreventDisplay] =
    useState(preventDisplay);

  useEffect(() => {
    let preventSetting = false;
    (async () => {
      await refreshOptimizeVariants({ triggerEvent: triggerEvent });
      if (!preventSetting) setGaMountedPreventDisplay(false);
    })();

    return () => {
      preventSetting = true;
    };
  }, []);

  const updatedChildren = useMemo(
    () =>
      Children.map(children, (child) => {
        if (isValidElement(child)) {
          const className = clsx(
            child.props.className,
            preventText && !optimizeMounted && classes.hideText,
            gaMountedPreventDisplay && !optimizeMounted && classes.hideContent,
          );

          return <child.type {...child.props} className={className} />;
        } else {
          // Not an element (possibly a text or boolean) just return it
          return child;
        }
      }),
    [
      children,
      preventText,
      preventDisplay,
      optimizeMounted,
      gaMountedPreventDisplay,
    ],
  );

  return <>{updatedChildren}</>;
};
